import React from 'react';

// Compute payment progress from a milestones array.
// Progress = sum of percentages of milestones marked as paid.
// Color reflects payment health based on OVERDUE unpaid milestones:
//   green  = on course (no unpaid milestone past its estimated date)
//   orange = an unpaid milestone is overdue by up to 1 month (<= 30 days)
//   red    = an unpaid milestone is overdue by more than 1 month (> 30 days)
export const computePaymentProgress = (milestones) => {
  if (!Array.isArray(milestones) || milestones.length === 0) return null;

  const percent = milestones
    .filter((m) => m && m.paid)
    .reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  let maxDaysOverdue = -1;
  milestones.forEach((m) => {
    if (m && !m.paid && m.estimated_date) {
      const d = new Date(m.estimated_date);
      if (!isNaN(d.getTime())) {
        const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));
        if (days > maxDaysOverdue) maxDaysOverdue = days;
      }
    }
  });

  let color = 'green';
  if (maxDaysOverdue > 30) color = 'red';
  else if (maxDaysOverdue > 0) color = 'orange';

  return {
    percent: Math.min(100, Math.round(percent)),
    color,
    maxDaysOverdue,
  };
};

const COLOR_MAP = {
  green: { bar: 'bg-green-500', text: 'text-green-700', bgLight: 'bg-green-100 border-green-200' },
  orange: { bar: 'bg-orange-500', text: 'text-orange-700', bgLight: 'bg-orange-100 border-orange-200' },
  red: { bar: 'bg-red-500', text: 'text-red-700', bgLight: 'bg-red-100 border-red-200' },
};

const buildTitle = (result) => {
  let t = `Payment progress: ${result.percent}% paid`;
  if (result.color === 'orange') t += ' • payment overdue (≤ 1 month)';
  else if (result.color === 'red') t += ' • payment overdue (> 1 month)';
  else t += ' • on course';
  return t;
};

// variant: 'bar' (full width progress bar) | 'compact' (small colored % badge)
export const PaymentProgress = ({ milestones, variant = 'bar', className = '' }) => {
  const result = computePaymentProgress(milestones);
  if (!result) return null;

  const c = COLOR_MAP[result.color];
  const title = buildTitle(result);

  if (variant === 'compact') {
    return (
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded border text-xs font-semibold ${c.bgLight} ${c.text} ${className}`}
        title={title}
        data-testid="payment-progress-compact"
      >
        {result.percent}%
      </span>
    );
  }

  return (
    <div className={`w-full ${className}`} title={title} data-testid="payment-progress-bar">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs text-gray-500 dark:text-gray-400">Payment</span>
        <span className={`text-xs font-semibold ${c.text}`}>{result.percent}%</span>
      </div>
      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${c.bar} transition-all duration-300`}
          style={{ width: `${result.percent}%` }}
        />
      </div>
    </div>
  );
};

export default PaymentProgress;
